import { toPng } from 'html-to-image';
import { waitForImageDecodes } from './photo';

// Self-hosted card fonts, pre-embedded as data URLs. Providing this to toPng's
// `fontEmbedCSS` makes html-to-image skip scanning document stylesheets — which
// otherwise throws a (caught but noisy) SecurityError when it hits the
// cross-origin Google Fonts sheet (Roboto/Merriweather), loudest in Safari.
const FONT_FACES: ReadonlyArray<{ family: string; weight: string; url: string }> = [
  { family: 'Cal Sans', weight: '400', url: '/fonts/calsans-400.woff2' },
  { family: 'Caveat', weight: '400 700', url: '/fonts/caveat-400.woff2' },
];

let fontEmbedCssPromise: Promise<string> | null = null;

function fontEmbedCss(): Promise<string> {
  if (!fontEmbedCssPromise) {
    fontEmbedCssPromise = Promise.all(
      FONT_FACES.map(async ({ family, weight, url }) => {
        const blob = await (await fetch(url)).blob();
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        });
        return `@font-face{font-family:'${family}';font-style:normal;font-weight:${weight};font-display:swap;src:url(${dataUrl}) format('woff2');}`;
      }),
    )
      .then((rules) => rules.join('\n'))
      .catch(() => {
        // Fall back to html-to-image's own scan (with its console noise) rather
        // than break the export if a font fetch fails.
        fontEmbedCssPromise = null;
        return '';
      });
  }
  return fontEmbedCssPromise;
}

/**
 * Rasterize a `<ReportCard variant="full">` element (rendered offscreen at its
 * natural 1080×1350) to a PNG data URL — the single per-episode shareable.
 *
 * Cal Sans + Caveat are embedded via a pre-built `fontEmbedCSS` (data URLs), so
 * the PNG has the display fonts without html-to-image scanning the cross-origin
 * Google stylesheet (Roboto/Merriweather still fall back to system fonts in the
 * export, which is fine for the body copy). Waits for fonts + the
 * bg/circle/marking images before capturing.
 */
export async function rasterizeReportCard(el: HTMLElement): Promise<string> {
  if (typeof document !== 'undefined' && document.fonts?.ready) {
    try {
      await document.fonts.ready;
    } catch {
      // Font readiness is best-effort; capture anyway.
    }
  }
  const [fontCss] = await Promise.all([fontEmbedCss(), waitForImageDecodes(el)]);
  return toPng(el, { cacheBust: true, pixelRatio: 1, fontEmbedCSS: fontCss });
}

/** Filename for a downloaded episode report card. */
export function quizShareFilename(episode: number): string {
  return `off-campus-episode-${episode}-report.png`;
}
